import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useRef, useState } from 'react'
import { api, ErreurApi, lire, requetes } from '../api'
import { Decompte } from '../composants/Decompte'
import { formaterInstant } from '../composants/Historique'
import { Icone } from '../composants/Icone'
import { Section, Vide } from '../composants/Section'
import { confirmer } from '../composants/confirmation'

// Réglages, « Ordinateurs » : les Mac où tourne le collecteur local (docs/collecteur-local.md).
// Chacun ne voit que les siens. Un ordinateur s'appaire par un code affiché en plein écran (dix
// minutes, un seul usage), saisi sur le Mac ; il se révoque s'il est perdu ou revendu. Les
// sessions des portails restent sur l'ordinateur : rien ici ne les montre. Ses connexions, à
// réattribuer, passent en un geste sur un autre de ses ordinateurs (changement de Mac).

/** « ABCD 2345 » : plus lisible à recopier. */
const espace = (code: string) => `${code.slice(0, 4)} ${code.slice(4)}`

/** Dossier de l'extension posé par l'installeur, à charger une fois dans Chrome. */
const EXTENSION = '~/Library/Application Support/Admin PMVB Collecteur/extension'

/** Un texte à coller ailleurs (Terminal, sélecteur de dossier), avec son bouton « Copier ». */
function ACopier({ texte, libelle }: { texte: string; libelle: string }) {
  const [copie, setCopie] = useState(false)
  return (
    <>
      <code className="adresse appairage-commande">{texte}</code>
      <button
        type="button"
        className="bouton-discret"
        onClick={async () => {
          try {
            await navigator.clipboard.writeText(texte)
            setCopie(true)
          } catch {
            setCopie(false)
          }
        }}
      >
        {copie ? 'Copié' : libelle}
      </button>
    </>
  )
}

function EcranAppairage({ avant, onFermer }: { avant: ReadonlySet<string>; onFermer: () => void }) {
  const client = useQueryClient()
  const [code, setCode] = useState<{ code: string; expireLe: string } | null>(null)
  const [erreur, setErreur] = useState<string | null>(null)
  const demande = useRef(false)
  // Relu toutes les trois secondes : l'écran annonce l'ordinateur dès qu'il s'est appairé.
  const ordinateurs = useQuery({ ...requetes.ordinateurs, refetchInterval: 3000 })
  const nouveau = (ordinateurs.data ?? []).find((o) => !avant.has(o.id))

  useEffect(() => {
    if (demande.current) return
    demande.current = true
    lire(api.ordinateurs.code.$post())
      .then((c) => setCode({ code: c.code, expireLe: String(c.expireLe) }))
      .catch((x: unknown) =>
        setErreur(x instanceof ErreurApi ? x.message : 'Code d’appairage indisponible.'),
      )
  }, [])

  return (
    <div className="defi-ecran" role="dialog" aria-modal="true" aria-labelledby="appairage-titre">
      <header className="defi-entete">
        <span className="defi-marque">
          <Icone nom="computer" taille={22} />
        </span>
        <div>
          <p className="defi-surtitre">Collecteur local</p>
          <h1 id="appairage-titre">Ajouter cet ordinateur</h1>
        </div>
        <button
          type="button"
          className="bouton-icone"
          onClick={() => {
            void client.invalidateQueries({ queryKey: ['ordinateurs'] })
            onFermer()
          }}
          aria-label="Fermer"
          title="Fermer"
        >
          <Icone nom="close" />
        </button>
      </header>
      <div className="defi-corps">
        {nouveau ? (
          <>
            <p className="defi-demande defi-grand" role="status">
              {nouveau.libelle} est appairé.
            </p>
            <p>Dernière étape, une seule fois : l’extension dans Chrome.</p>
            <ol className="etapes appairage-etapes">
              <li>
                Dans Chrome, ouvrez <code>chrome://extensions</code> et activez « Mode développeur
                », en haut à droite.
              </li>
              <li>
                « Charger l’extension non empaquetée », puis Cmd + Maj + G, collez ce chemin et «
                Sélectionner » (déjà chargée : ↻) :
                <ACopier texte={EXTENSION} libelle="Copier le chemin" />
              </li>
              <li>
                Dans « Portails », choisissez ce Mac dans « Relever sur » pour chaque connexion à
                relever ici.
              </li>
            </ol>
          </>
        ) : (
          <>
            <ol className="etapes appairage-etapes">
              <li>
                Une fois sur ce Mac : Google Chrome et{' '}
                <a href="https://nodejs.org" target="_blank" rel="noreferrer">
                  Node.js
                </a>{' '}
                (version LTS) installés.
              </li>
              <li>
                Ouvrez le Terminal (Cmd + Espace, « Terminal »), collez cette ligne puis Entrée :
                elle installe le collecteur, ou le met à jour, et appaire ce Mac.
                {code ? (
                  <ACopier
                    texte={`curl -fsSL -d code=${code.code} ${window.location.origin}/api/agent/installation | bash`}
                    libelle="Copier la ligne"
                  />
                ) : (
                  !erreur && <span className="discret"> Préparation de la ligne…</span>
                )}
              </li>
            </ol>
            {code && (
              <p className="discret">
                Elle ne sert qu’une fois. <Decompte jusqua={code.expireLe} className="" />. Code de
                ce Mac, si on vous le demande :{' '}
                <span className="appairage-code">{espace(code.code)}</span>. Aucun identifiant de
                portail ne passe par ici : vous vous connecterez vous-même sur le Mac.
              </p>
            )}
          </>
        )}
        {erreur && <p className="alerte">{erreur}</p>}
      </div>
    </div>
  )
}

export function Ordinateurs() {
  const client = useQueryClient()
  const ordinateurs = useQuery(requetes.ordinateurs)
  const [appairage, setAppairage] = useState<ReadonlySet<string> | null>(null)
  const [message, setMessage] = useState<string | null>(null)
  const liste = ordinateurs.data ?? []
  const actifs = liste.filter((o) => !o.revoqueLe)
  const agir = async (appel: () => Promise<unknown>, reussite: string, echec: string) => {
    try {
      await appel()
      setMessage(reussite)
    } catch (x) {
      setMessage(x instanceof ErreurApi ? x.message : echec)
    } finally {
      await client.invalidateQueries({ queryKey: ['ordinateurs'] })
      await client.invalidateQueries({ queryKey: ['portails'] })
    }
  }
  return (
    <Section titre="Ordinateurs">
      <p className="discret">
        Le collecteur local relève, sur votre Mac et avec vos sessions, les portails qui refusent un
        serveur (EDF…). Liez ensuite une connexion à un ordinateur dans « Portails ».
      </p>
      {message && (
        <p className="discret" role="status">
          {message}
        </p>
      )}
      {ordinateurs.data && liste.length === 0 && <Vide>Aucun ordinateur appairé.</Vide>}
      {liste.map((o) => (
        <div key={o.id} className="carte-banque">
          <div className="ligne">
            <div className="ligne-contenu">
              <strong>{o.libelle}</strong>{' '}
              {o.revoqueLe && <span className="badge retard-en_retard">Révoqué</span>}
              <div className="discret">
                macOS · version {o.version}
                {o.dernierContactLe
                  ? ` · dernier contact le ${formaterInstant(String(o.dernierContactLe))}`
                  : ''}
              </div>
              <div className="discret">
                {o.connexions.length === 0
                  ? 'Aucune connexion liée'
                  : `${o.revoqueLe ? 'À réattribuer' : 'Relève'} : ${o.connexions.map((c) => c.libelle).join(', ')}`}
              </div>
            </div>
            {!o.revoqueLe && (
              <div className="boutons">
                <button
                  type="button"
                  className="bouton-discret"
                  onClick={async () => {
                    if (
                      await confirmer(
                        `Révoquer ${o.libelle} ? Il ne sera plus reconnu ; ses connexions seront à réattribuer à un autre ordinateur.`,
                      )
                    )
                      void agir(
                        () => lire(api.ordinateurs[':id'].revoquer.$post({ param: { id: o.id } })),
                        `${o.libelle} révoqué.`,
                        'Révocation impossible.',
                      )
                  }}
                >
                  Révoquer
                </button>
              </div>
            )}
            {o.revoqueLe && o.connexions.length > 0 && (
              <div className="boutons">
                {actifs.map((vers) => (
                  <button
                    key={vers.id}
                    type="button"
                    className="bouton"
                    onClick={() =>
                      agir(
                        () =>
                          lire(
                            api.ordinateurs[':id'].reprendre.$post({
                              param: { id: vers.id },
                              json: { depuisId: o.id },
                            }),
                          ),
                        `Connexions de ${o.libelle} reprises sur ${vers.libelle}.`,
                        'Reprise impossible.',
                      )
                    }
                  >
                    Reprendre sur {vers.libelle}
                  </button>
                ))}
                {actifs.length === 0 && (
                  <p className="discret">
                    Ajoutez le nouvel ordinateur pour y reprendre ces connexions.
                  </p>
                )}
              </div>
            )}
          </div>
        </div>
      ))}
      <div className="boutons">
        <button
          type="button"
          className="bouton"
          onClick={() => setAppairage(new Set(liste.map((o) => o.id)))}
        >
          Ajouter un ordinateur
        </button>
      </div>
      {appairage && <EcranAppairage avant={appairage} onFermer={() => setAppairage(null)} />}
    </Section>
  )
}
